import { useEffect, useId, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowLeft, CircleCheck, FileDown, Loader2, Pencil, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { canDocflow } from "@/auth/portal";
import {
  generateDealDocFn,
  loadDeal,
  loadDealOptions,
  loadUpdFn,
  markArrivedFn,
  refreshKitTextsFn,
  type UpdResult,
  moveDealFn,
  updateDealFn,
  type DealView,
} from "@/braining/deals.functions";
import {
  closingDocs,
  contractAmount,
  DEAL_KIND_LABEL,
  PAYMENT_LABEL,
  monthStages,
  type DealStage,
  type PaymentScheme,
  DEAL_STATUS_LABEL,
  MARKING_LABEL,
  monthsOf,
  MOVE_LABEL,
  NEXT,
  type DealDraft,
  type DealProblems,
  type DealStatus,
  type MarkingBy,
  closingOpen,
  PAPER_CARRIER_LABEL,
  trackUrl,
  PARENT_KINDS,
  CHANGE_MODE_LABEL,
  type ChangeMode,
  type DealChange,
} from "@/braining/deals";
import { downloadDocx } from "@/braining/components/download-docx";
import { UpdPreview } from "@/braining/components/upd-preview";
import { DocEditor } from "@/braining/components/doc-editor";
import type { Deal } from "@/braining/deals-store";
import { KIND_LABEL } from "@/braining/partners";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Комплект документов с подрядчиком: договор, приложение или ДС, от черновика до
 * закрытия. Документы пока не генерируются — шаблоны Даши ещё не пришли;
 * здесь всё, что нужно, чтобы их потом собрать: юрлицо, проект, услуги,
 * сроки, сумма, маркировка, и путь по статусам с историей.
 */
export const Route = createFileRoute("/deal/$id")({
  head: () => ({ meta: [{ title: "Комплект документов — Портал Braining Group" }] }),
  beforeLoad: ({ context }) => {
    if (!canDocflow(context.user?.role)) throw redirect({ to: "/" });
  },
  loader: ({ params }) => loadDeal({ data: { id: params.id } }),
  component: DealPage,
});

function DealPage() {
  const view = Route.useLoaderData();
  if (!view) return <p className="text-sm text-muted-foreground">Комплект не найден.</p>;
  return <DealView initial={view} />;
}

type Options = Awaited<ReturnType<typeof loadDealOptions>>;

const rub = (n: number | null) =>
  n == null ? "—" : `${n.toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;

function toDraft(d: Deal): DealDraft {
  return {
    contractorId: d.contractorId,
    entityId: d.entityId,
    project: d.project,
    managerId: d.managerId,
    services: d.services,
    kind: d.kind,
    contractNo: d.contractNo,
    annexNo: d.annexNo,
    periodFrom: d.periodFrom,
    periodTo: d.periodTo,
    amountNet: d.amountNet,
    taxMarkup: d.taxMarkup,
    ad: d.ad,
    markingBy: d.markingBy,
    note: d.note,
    parentId: d.parentId,
    contractDate: d.contractDate,
    tender: d.tender,
    paymentScheme: d.paymentScheme,
    prepayPct: d.prepayPct,
    stages: d.stages,
    noNda: d.noNda,
    withAct: d.withAct,
    withFrame: d.withFrame,
    changes: d.changes,
    effectiveFrom: d.effectiveFrom,
  };
}

function DealView({ initial }: { initial: DealView }) {
  const uid = useId();
  const [view, setView] = useState(initial);
  const deal = view.deal;
  const [draft, setDraft] = useState<DealDraft>(() => toDraft(initial.deal));
  const [amount, setAmount] = useState(initial.deal.amountNet?.toString() ?? "");
  const [markup, setMarkup] = useState(initial.deal.taxMarkup?.toString() ?? "");
  const [problems, setProblems] = useState<DealProblems>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [options, setOptions] = useState<Options | null>(null);

  useEffect(() => {
    loadDealOptions()
      .then(setOptions)
      .catch((e: unknown) => console.error("[deal] справочники не загрузились", e));
  }, []);

  const editable = deal.status === "draft" || deal.status === "approval";
  const num = (s: string) => {
    const v = s.trim().replace(/\s+/g, "").replace(",", ".");
    return v === "" ? null : Number(v);
  };
  const current: DealDraft = { ...draft, amountNet: num(amount), taxMarkup: num(markup) };
  const dirty = JSON.stringify(current) !== JSON.stringify(toDraft(deal));
  const total = contractAmount(current.amountNet, current.taxMarkup);
  const set = <K extends keyof DealDraft>(k: K, v: DealDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  function adopt(next: Deal) {
    setView((v) => ({ ...v, deal: next }));
    setDraft(toDraft(next));
    setAmount(next.amountNet?.toString() ?? "");
    setMarkup(next.taxMarkup?.toString() ?? "");
  }

  async function refresh() {
    const v = await loadDeal({ data: { id: deal.id } });
    if (v) {
      setView(v);
      adopt(v.deal);
    }
  }

  async function save() {
    setBusy("save");
    setError(null);
    try {
      const res = await updateDealFn({
        data: { id: deal.id, draft: current, seen: deal.updatedAt },
      });
      if (!res.ok) {
        setError(res.error);
        setProblems(res.problems ?? {});
        return;
      }
      setProblems({});
      adopt(res.deal);
      await refresh();
      toast.success("Сохранено");
    } catch (e) {
      console.error("[deal] не сохранилась", e);
      setError("Не удалось сохранить");
    } finally {
      setBusy(null);
    }
  }

  async function move(to: DealStatus) {
    if (dirty) {
      setError("Сначала сохраните изменения");
      return;
    }
    setBusy(to);
    setError(null);
    try {
      const res = await moveDealFn({ data: { id: deal.id, to, seen: deal.updatedAt } });
      if (!res.ok) {
        setError(res.error);
        setProblems(res.problems ?? {});
        return;
      }
      setProblems({});
      await refresh();
      toast.success(DEAL_STATUS_LABEL[to]);
    } catch (e) {
      console.error("[deal] статус не сменился", e);
      setError("Не удалось сменить статус");
    } finally {
      setBusy(null);
    }
  }

  async function refreshTexts() {
    setBusy("texts");
    try {
      const r = await refreshKitTextsFn({ data: { id: deal.id } });
      if (!r.ok) toast.error(r.error);
      else {
        await refresh();
        toast.success("Комплект собирается по текущим шаблонам");
      }
    } finally {
      setBusy(null);
    }
  }

  const forward = NEXT[deal.status].filter((s) => MOVE_LABEL[`${deal.status}>${s}`]);
  const back = NEXT[deal.status].filter((s) => !MOVE_LABEL[`${deal.status}>${s}`]);
  const months = draft.ad ? monthsOf(draft.periodFrom, draft.periodTo) : [];
  const child = draft.kind === "annex" || draft.kind === "supplement";

  const field = (key: keyof DealDraft, label: string, node: React.ReactNode, wide = false) => (
    <div className={cn("space-y-1.5", wide && "sm:col-span-2")}>
      <Label htmlFor={`${uid}-${key}`} className="text-xs font-normal text-muted-foreground">
        {label}
      </Label>
      {node}
      {problems[key] && <p className="text-xs text-destructive-ink">{problems[key]}</p>}
    </div>
  );

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <Link
          to="/deals"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Комплекты документов
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">{deal.title}</h1>
          <Badge variant="outline" className="font-normal">
            {DEAL_STATUS_LABEL[deal.status]}
          </Badge>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          <Link
            to="/contractor/$id"
            params={{ id: deal.contractorId }}
            className="hover:text-foreground hover:underline"
          >
            {deal.contractorName}
          </Link>{" "}
          · {KIND_LABEL[deal.contractorKind]}
          {deal.entityName && ` · ${deal.entityName}`}
          {deal.project && ` · проект ${deal.project}`}
          {deal.managerEmail && ` · ведёт ${deal.managerEmail}`}
          {deal.tender && " · тендер"}
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-wrap items-center gap-2">
            {forward.map((s) => (
              <Button key={s} onClick={() => void move(s)} disabled={busy !== null}>
                {busy === s && <Loader2 className="size-4 animate-spin" />}
                {MOVE_LABEL[`${deal.status}>${s}`]}
              </Button>
            ))}
            {back.map((s) => (
              <Button
                key={s}
                variant={s === "cancelled" ? "ghost" : "outline"}
                onClick={() => void move(s)}
                disabled={busy !== null}
              >
                {s !== "cancelled" && <Undo2 className="size-4" />}
                {s === "cancelled"
                  ? "Аннулировать"
                  : `Назад: ${DEAL_STATUS_LABEL[s].toLowerCase()}`}
              </Button>
            ))}
          </div>
          {deal.status === "approval" && (
            <p className="text-xs text-muted-foreground">
              Документы уходят АЛ письмом, бухгалтерия в копии. Его «ок» в почте — и дальше к
              подрядчику.
            </p>
          )}
          {(deal.kind === "one_off" || deal.kind === "annex") && (
            <p className="text-sm text-muted-foreground">
              Закрывающие: {closingDocs(deal.contractorKind, deal.contractorDocFlow, draft.ad)}.
            </p>
          )}
          {months.length > 1 && !draft.stages.length && (
            <p className="text-sm text-warning-ink">
              Размещение переходит через месяц ({months.length} мес.) — разбейте сумму по месяцам
              ниже: акт будет за каждый месяц.
            </p>
          )}
          <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            {deal.templateAt
              ? `Тексты шаблонов зафиксированы ${new Date(deal.templateAt).toLocaleDateString("ru-RU")} — правки в «Шаблонах» этот комплект не меняют.`
              : "Черновик собирается по текущим шаблонам; тексты зафиксируются, когда комплект уйдёт АЛ."}
            {deal.templateAt && deal.status === "approval" && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy !== null}
                onClick={() => void refreshTexts()}
              >
                Взять текущие шаблоны
              </Button>
            )}
          </p>
          {deal.kind !== "frame" && !deal.noNda && (
            <p className="text-sm text-muted-foreground">
              В комплект — NDA с датой договора (если с этим подрядчиком по этому юрлицу его ещё не
              подписывали).
            </p>
          )}
        </CardContent>
      </Card>

      <DealDocs deal={deal} dirty={dirty} edited={view.edited} onEdited={() => void refresh()} />

      {deal.upd && <UpdCard deal={deal} />}

      {(deal.kind === "one_off" || deal.kind === "annex") && closingOpen(deal.status) && (
        <PaperCard
          deal={deal}
          onChange={async () => {
            const next = await loadDeal({ data: { id: deal.id } });
            if (next) setView((v) => ({ ...v, deal: next.deal }));
          }}
        />
      )}

      <Card>
        <CardContent className="space-y-4 p-5">
          <fieldset disabled={!editable} className="grid gap-3 sm:grid-cols-2">
            <p className="text-sm sm:col-span-2">
              <span className="text-muted-foreground">Документ: </span>
              {DEAL_KIND_LABEL[draft.kind]}
            </p>
            {field(
              "project",
              "ID проекта",
              <Input
                id={`${uid}-project`}
                value={draft.project}
                placeholder="как в таблице и реестрах"
                onChange={(e) => set("project", e.target.value)}
              />,
            )}
            {field(
              "managerId",
              "Кто ведёт — передать комплект другому менеджеру",
              <Select
                value={draft.managerId ?? undefined}
                onValueChange={(v) => set("managerId", v)}
                disabled={!editable}
              >
                <SelectTrigger id={`${uid}-managerId`} className="w-full">
                  <SelectValue placeholder="Выберите" />
                </SelectTrigger>
                <SelectContent>
                  {(options?.people ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>,
            )}
            {field(
              "entityId",
              "Юрлицо агентства — как у проекта с клиентом",
              <Select
                value={draft.entityId ?? undefined}
                onValueChange={(v) => set("entityId", v)}
                disabled={!editable || child}
              >
                <SelectTrigger id={`${uid}-entityId`} className="w-full">
                  <SelectValue placeholder="Выберите" />
                </SelectTrigger>
                <SelectContent>
                  {(options?.entities ?? []).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>,
            )}
            {child &&
              field(
                "parentId",
                draft.kind === "annex" ? "К чему приложение" : "К чему ДС",
                <Select
                  value={draft.parentId ?? undefined}
                  onValueChange={(v) => set("parentId", v)}
                  disabled={!editable || deal.withFrame}
                >
                  <SelectTrigger id={`${uid}-parentId`} className="w-full">
                    <SelectValue placeholder="Выберите" />
                  </SelectTrigger>
                  <SelectContent>
                    {view.parents
                      .filter((p) => PARENT_KINDS[draft.kind].includes(p.kind))
                      .map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.title}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>,
                true,
              )}
            <div className="grid grid-cols-2 gap-3 sm:col-span-2">
              {field(
                "contractDate",
                draft.kind === "annex"
                  ? "Дата приложения"
                  : draft.kind === "supplement"
                    ? "Дата ДС"
                    : "Дата договора — по умолчанию начало услуг",
                <Input
                  id={`${uid}-contractDate`}
                  type="date"
                  value={draft.contractDate ?? ""}
                  onChange={(e) => set("contractDate", e.target.value || null)}
                />,
              )}
              {child
                ? field(
                    "annexNo",
                    draft.kind === "annex" ? "№ приложения" : "№ ДС",
                    <Input
                      id={`${uid}-annexNo`}
                      value={draft.annexNo}
                      placeholder="следующий по родителю"
                      onChange={(e) => set("annexNo", e.target.value)}
                    />,
                  )
                : field(
                    "contractNo",
                    "№ договора",
                    <Input
                      id={`${uid}-contractNo`}
                      value={draft.contractNo}
                      placeholder="по дате — сам"
                      onChange={(e) => set("contractNo", e.target.value)}
                    />,
                  )}
            </div>
            {draft.kind === "supplement" && (
              <>
                <div className="space-y-1.5 sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Что меняем</p>
                  <ChangesEditor
                    changes={draft.changes}
                    disabled={!editable}
                    onChange={(next) => set("changes", next)}
                  />
                  {problems.changes && (
                    <p className="text-xs text-destructive-ink">{problems.changes}</p>
                  )}
                </div>
                {field(
                  "effectiveFrom",
                  "Действует с (обратная сила) — пусто, если с подписания",
                  <Input
                    id={`${uid}-effectiveFrom`}
                    type="date"
                    value={draft.effectiveFrom ?? ""}
                    onChange={(e) => set("effectiveFrom", e.target.value || null)}
                  />,
                )}
              </>
            )}
            {(draft.kind === "one_off" || draft.kind === "annex") && (
              <>
                {field(
                  "services",
                  "Услуги — как в договоре: «услуги по … в рамках реализации проекта для клиента «…»», состав — строками ниже",
                  <Textarea
                    id={`${uid}-services`}
                    value={draft.services}
                    rows={3}
                    onChange={(e) => set("services", e.target.value)}
                  />,
                  true,
                )}
                {field(
                  "periodFrom",
                  "Услуги с",
                  <Input
                    id={`${uid}-periodFrom`}
                    type="date"
                    value={draft.periodFrom ?? ""}
                    onChange={(e) => {
                      const v = e.target.value || null;
                      // Дата договора по умолчанию — начало услуг (созвон 28.10).
                      setDraft((d) => ({
                        ...d,
                        periodFrom: v,
                        contractDate:
                          !d.contractDate || d.contractDate === d.periodFrom ? v : d.contractDate,
                      }));
                    }}
                  />,
                )}
                {field(
                  "periodTo",
                  "по",
                  <Input
                    id={`${uid}-periodTo`}
                    type="date"
                    value={draft.periodTo ?? ""}
                    onChange={(e) => set("periodTo", e.target.value || null)}
                  />,
                )}
                {field(
                  "amountNet",
                  "Сумма «на руки», ₽",
                  <Input
                    id={`${uid}-amountNet`}
                    value={amount}
                    inputMode="decimal"
                    onChange={(e) => setAmount(e.target.value)}
                  />,
                )}
                {field(
                  "taxMarkup",
                  "Налог подрядчика, %",
                  <Input
                    id={`${uid}-taxMarkup`}
                    value={markup}
                    inputMode="decimal"
                    placeholder="из карточки подрядчика"
                    onChange={(e) => setMarkup(e.target.value)}
                  />,
                )}
                <div className="space-y-1.5 sm:col-span-2">
                  <p className="text-sm">
                    Сумма договора: <span className="font-medium">{rub(total)}</span>
                    {current.taxMarkup && current.amountNet != null ? (
                      <span className="text-muted-foreground">
                        {" "}
                        — {rub(current.amountNet)} на руки ÷ (1 − {current.taxMarkup}%), чтобы после
                        налога подрядчику осталось договорённое
                      </span>
                    ) : null}
                  </p>
                </div>
                {field(
                  "paymentScheme",
                  "Оплата",
                  <Select
                    value={draft.paymentScheme}
                    onValueChange={(v) => set("paymentScheme", v as PaymentScheme)}
                    disabled={!editable}
                  >
                    <SelectTrigger id={`${uid}-paymentScheme`} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PAYMENT_LABEL) as PaymentScheme[]).map((k) => (
                        <SelectItem key={k} value={k}>
                          {PAYMENT_LABEL[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>,
                )}
                {draft.paymentScheme === "split" &&
                  field(
                    "prepayPct",
                    `Аванс, % — ${rub(total == null ? null : Math.round((total * draft.prepayPct) / 100))}`,
                    <Input
                      id={`${uid}-prepayPct`}
                      type="number"
                      min={1}
                      max={99}
                      value={draft.prepayPct}
                      onChange={(e) => set("prepayPct", Number(e.target.value) || 50)}
                    />,
                  )}
                <div className="space-y-2 sm:col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={draft.ad}
                      onCheckedChange={(v) => {
                        set("ad", v === true);
                        if (v !== true) set("stages", []);
                      }}
                      disabled={!editable}
                    />
                    Рекламное продвижение — пункт о маркировке, акты по календарным месяцам
                  </label>
                  {draft.ad && (
                    <Select
                      value={draft.markingBy}
                      onValueChange={(v) => set("markingBy", v as MarkingBy)}
                      disabled={!editable}
                    >
                      <SelectTrigger className="w-full sm:w-80">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(MARKING_LABEL) as MarkingBy[]).map((k) => (
                          <SelectItem key={k} value={k}>
                            {MARKING_LABEL[k]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {draft.ad && months.length > 1 && (
                    <StagesEditor
                      stages={draft.stages}
                      total={total}
                      problem={problems.stages}
                      onSplit={() => set("stages", monthStages(draft.periodFrom, draft.periodTo))}
                      onChange={(next) => set("stages", next)}
                    />
                  )}
                </div>
              </>
            )}
            <div className="space-y-2 sm:col-span-2">
              <p className="text-xs text-muted-foreground">Комплектация</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {(draft.kind === "one_off" || draft.kind === "annex") && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={draft.withAct}
                      onCheckedChange={(v) => set("withAct", v === true)}
                      disabled={!editable}
                    />
                    Акт
                  </label>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!draft.noNda}
                    onCheckedChange={(v) => set("noNda", v !== true)}
                    disabled={!editable}
                  />
                  NDA
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={draft.tender}
                    onCheckedChange={(v) => set("tender", v === true)}
                    disabled={!editable}
                  />
                  Тендер — в комплект служебная записка
                </label>
              </div>
            </div>
            {field(
              "note",
              "Заметка для своих",
              <Textarea
                id={`${uid}-note`}
                value={draft.note}
                rows={2}
                onChange={(e) => set("note", e.target.value)}
              />,
              true,
            )}
          </fieldset>
          {!editable && (
            <p className="text-xs text-muted-foreground">
              Комплект уже у подрядчика — чтобы поправить поля, верните его на согласование.
            </p>
          )}
          {error && <p className="text-sm text-destructive-ink">{error}</p>}
          {editable && (
            <Button onClick={() => void save()} disabled={busy !== null || !dirty}>
              {busy === "save" && <Loader2 className="size-4 animate-spin" />}
              Сохранить
            </Button>
          )}
        </CardContent>
      </Card>

      {deal.events.length > 0 && (
        <Card>
          <CardContent className="space-y-1.5 p-5 text-sm">
            <h2 className="mb-1 font-medium">История</h2>
            {[...deal.events].reverse().map((e, i) => (
              <p key={i} className="text-muted-foreground">
                {new Date(e.at).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })}{" "}
                · {e.by} · {e.what}
              </p>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Изменения в ДС — как в ДС корпуса: «изложить пункт в новой редакции»,
 * «заменить текст … на …» или сведения без пункта.
 */
function ChangesEditor({
  changes,
  disabled,
  onChange,
}: {
  changes: DealChange[];
  disabled: boolean;
  onChange: (next: DealChange[]) => void;
}) {
  const put = (i: number, patch: Partial<DealChange>) =>
    onChange(changes.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  return (
    <div className="space-y-2">
      {changes.map((c, i) => (
        <div key={i} className="space-y-2 rounded-md border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">{i + 1}.</span>
            <select
              value={c.mode}
              disabled={disabled}
              onChange={(e) => put(i, { mode: e.target.value as ChangeMode })}
              className="h-8 rounded-md border bg-background px-2 text-sm"
              aria-label="Как меняем"
            >
              {(Object.keys(CHANGE_MODE_LABEL) as ChangeMode[]).map((m) => (
                <option key={m} value={m}>
                  {CHANGE_MODE_LABEL[m]}
                </option>
              ))}
            </select>
            {c.mode !== "info" && (
              <Input
                className="h-8 w-28"
                value={c.clause}
                placeholder="пункт, 2.1"
                disabled={disabled}
                onChange={(e) => put(i, { clause: e.target.value })}
              />
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="ml-auto"
              disabled={disabled}
              onClick={() => onChange(changes.filter((_, j) => j !== i))}
            >
              Убрать
            </Button>
          </div>
          {c.mode === "replace" && (
            <Textarea
              rows={2}
              value={c.oldText}
              placeholder="Было — текст, который заменяем"
              disabled={disabled}
              onChange={(e) => put(i, { oldText: e.target.value })}
            />
          )}
          <Textarea
            rows={2}
            value={c.newText}
            placeholder={
              c.mode === "info"
                ? "Например: Исполнитель информирует Заказчика о переходе на УСН с 01.01.2026 г."
                : "Стало — новый текст"
            }
            disabled={disabled}
            onChange={(e) => put(i, { newText: e.target.value })}
          />
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() =>
          onChange([...changes, { mode: "restate", clause: "", oldText: "", newText: "" }])
        }
      >
        Добавить изменение
      </Button>
    </div>
  );
}

/** Этапы по месяцам: границы по концу месяца, суммы вписывает менеджер. */
function StagesEditor({
  stages,
  total,
  problem,
  onSplit,
  onChange,
}: {
  stages: DealStage[];
  total: number | null;
  problem?: string;
  onSplit: () => void;
  onChange: (next: DealStage[]) => void;
}) {
  const fmt = (d: string) => d.split("-").reverse().join(".");
  if (!stages.length) {
    return (
      <Button type="button" size="sm" variant="outline" onClick={onSplit}>
        Разбить по месяцам
      </Button>
    );
  }
  const sum = stages.reduce((a, st) => a + (st.amount ?? 0), 0);
  return (
    <div className="space-y-2 rounded-md border p-3">
      {stages.map((st, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="w-56 text-muted-foreground">
            Этап {i + 1}: {fmt(st.from)} — {fmt(st.to)}
          </span>
          <Input
            className="h-8 w-36"
            inputMode="decimal"
            value={st.amount ?? ""}
            placeholder="сумма, ₽"
            onChange={(e) => {
              const v = e.target.value.replace(/\s+/g, "").replace(",", ".");
              const next = [...stages];
              next[i] = { ...st, amount: v === "" ? null : Number(v) };
              onChange(next);
            }}
          />
        </div>
      ))}
      <p className={cn("text-xs", problem ? "text-destructive-ink" : "text-muted-foreground")}>
        {problem ??
          `Этапов на ${Math.round(sum).toLocaleString("ru-RU")} ₽ из ${total == null ? "—" : total.toLocaleString("ru-RU")} ₽`}
      </p>
    </div>
  );
}

/**
 * Документы комплекта — docx для правки и отправки: рамочный договор (если
 * заведён вместе с приложением), договор, приложение или ДС, акты (по
 * одному на этап, если акт в комплектации), NDA (если в комплектации). Собираются из сохранённого комплекта, поэтому несохранённые
 * правки сначала нужно сохранить.
 */
function DealDocs({
  deal,
  dirty,
  edited,
  onEdited,
}: {
  deal: Deal;
  dirty: boolean;
  edited: string[];
  onEdited: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    doc: "contract" | "frame" | "act" | "nda";
    actNo?: number;
    label: string;
  } | null>(null);
  const closable = deal.kind === "one_off" || deal.kind === "annex";
  const acts = !closable || !deal.withAct ? 0 : deal.stages.length > 1 ? deal.stages.length : 1;
  const main =
    deal.kind === "frame"
      ? "Рамочный договор"
      : deal.kind === "annex"
        ? "Приложение"
        : deal.kind === "supplement"
          ? "Доп. соглашение"
          : "Договор";

  async function get(doc: "contract" | "frame" | "act" | "nda", actNo?: number) {
    const key = `${doc}${actNo ?? ""}`;
    setBusy(key);
    try {
      const res = await generateDealDocFn({ data: { id: deal.id, doc, actNo } });
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      downloadDocx(res.name, res.base64);
    } catch (e) {
      console.error("[deal] документ не собрался", e);
      toast.error("Не удалось собрать документ");
    } finally {
      setBusy(null);
    }
  }

  const btn = (key: string, label: string, onClick: () => void) => {
    const doc = key.replace(/\d+$/, "") as "contract" | "frame" | "act" | "nda";
    const actNo = doc === "act" ? Number(key.slice(3)) : undefined;
    const isEdited = edited.includes(doc === "act" ? `act:${actNo}` : doc);
    return (
      <div key={key} className="flex">
        <Button
          variant="outline"
          size="sm"
          className="rounded-r-none"
          onClick={onClick}
          disabled={busy !== null || dirty}
          title={isEdited ? "Скачается правленая версия" : undefined}
        >
          {busy === key ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <FileDown className="size-4" />
          )}
          {label}
          {isEdited && <span className="text-xs text-muted-foreground">· правлен</span>}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="rounded-l-none border-l-0 px-2"
          onClick={() => setEditing({ doc, actNo, label })}
          disabled={busy !== null || dirty}
          aria-label={`Править: ${label}`}
          title="Править текст документа"
        >
          <Pencil className="size-3.5" />
        </Button>
      </div>
    );
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <h2 className="font-medium">Документы</h2>
        <div className="flex flex-wrap gap-2">
          {deal.withFrame && btn("frame", "Рамочный договор", () => void get("frame"))}
          {btn("contract", main, () => void get("contract"))}
          {Array.from({ length: acts }, (_, i) =>
            btn(`act${i + 1}`, acts > 1 ? `Акт ${i + 1}` : "Акт", () => void get("act", i + 1)),
          )}
          {!deal.noNda && btn("nda", "NDA", () => void get("nda"))}
        </div>
        <p className="text-xs text-muted-foreground">
          {dirty
            ? "Сначала сохраните изменения — документы собираются из сохранённого комплекта."
            : "Word-файлы по практике агентства. Карандаш — поправить текст прямо здесь, включая шаблонный; правленая версия скачивается и у подрядчика. Пустые места отмечены подчёркиванием."}
        </p>
        {editing && (
          <DocEditor
            dealId={deal.id}
            doc={editing.doc}
            actNo={editing.actNo}
            label={editing.label}
            onClose={(changed) => {
              setEditing(null);
              if (changed) onEdited();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Оригиналы на бумаге: подрядчик вписывает трек в кабинете, здесь видно,
 * что едет, и отмечается «приехали» — чтобы передать офис-менеджеру
 * (созвон с Дашей 28.10). Подрядчику на ЭДО не нужно.
 */
function PaperCard({ deal, onChange }: { deal: Deal; onChange: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const p = deal.paper;
  if ((deal.contractorDocFlow ?? "").startsWith("ЭДО") && !p.sentAt) return null;

  async function mark(arrived: boolean) {
    setBusy(true);
    try {
      const res = await markArrivedFn({ data: { id: deal.id, arrived } });
      if (!res.ok) toast.error(res.error);
      else await onChange();
    } catch (e) {
      console.error("[deal] отметка не сохранилась", e);
      toast.error("Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  const url = trackUrl(p.carrier, p.track);
  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <h2 className="font-medium">Оригиналы</h2>
        {p.arrivedAt ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <CircleCheck className="size-4 text-success-ink" />
            Приехали {new Date(p.arrivedAt).toLocaleDateString("ru-RU")} — передать офис-менеджеру.
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void mark(false)}>
              <Undo2 className="size-4" />
              Снять отметку
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {p.sentAt ? (
              <span>
                Едут: {p.carrier ? PAPER_CARRIER_LABEL[p.carrier] : "—"}
                {p.track && " · "}
                {p.track &&
                  (url ? (
                    <a href={url} target="_blank" rel="noreferrer" className="underline">
                      {p.track}
                    </a>
                  ) : (
                    p.track
                  ))}
                <span className="text-muted-foreground">
                  {" "}
                  · отправлены {new Date(p.sentAt).toLocaleDateString("ru-RU")}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                Подрядчик ещё не вписал трек — акт он скачивает в своём кабинете.
              </span>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void mark(true)}>
              {busy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <CircleCheck className="size-4" />
              )}
              Приехали
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * УПД, который подрядчик сформировал в кабинете: открыть то же самое и
 * проверить, пока он не отправил его в ЭДО (созвон с Дашей 28.10).
 */
function UpdCard({ deal }: { deal: Deal }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<Extract<UpdResult, { ok: true }> | null>(null);
  const u = deal.upd!;

  async function open() {
    setBusy(true);
    try {
      const r = await loadUpdFn({ data: { id: deal.id } });
      if (!r.ok) toast.error(r.error);
      else setRes(r);
    } catch (e) {
      console.error("[deal] УПД не открылся", e);
      toast.error("Не удалось открыть УПД");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-medium">УПД</h2>
          <span className="text-sm text-muted-foreground">
            № {u.no} от {u.date.split("-").reverse().join(".")} · сформирован подрядчиком{" "}
            {u.at ? new Date(u.at).toLocaleDateString("ru-RU") : ""}
          </span>
          {!res && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              disabled={busy}
              onClick={() => void open()}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              Проверить
            </Button>
          )}
        </div>
        {res && <UpdPreview view={res.view} fileName={res.fileName} base64={res.base64} />}
      </CardContent>
    </Card>
  );
}
